import isWellFormedUrlPattern from 'routing/isWellFormedUrlPattern';
import {RouteBundle} from 'routing/RouteConstants';

/**
 * This code searched through an array of `RouteBundle` objects to find `urlPattern` values, p1, p2, on the
 * `Routes` in the objects, that meet the following conditions:
 *  a) p1 is not in the same `RouteBundle` object as p2
 *  b) it is not the case that both p1 and p2 are part of `legacy: true` `Route` objects, and
 *  c) p1 and p2 'clash' -- i.e., there is a string that matches to both patterns.
 *
 * The number of pairs to check for clashes is O(n^2), where n is the number of routes. This is worrisome.
 * However, this code has tested well for n = 168, and a total number of pairs of 168^2. It is unlikely that
 * the number of pairs this code in fact checks will reach 168^2 any time in the near future.
 *
 * @param routeBundles an array of `RouteBundle` objects.
 * @returns an array, each item of which represents a single pair of clashing urlPatterns. The array contains
 * item of the following form:
 *  [[<urlPattern>, <domain>], [<urlPattern, <domain>]]
 * A <urlPattern> is a `urlPattern` value on a `Route` object; a <domain> is the `domain` property on the
 * `RouteBundle` object to which the relevant `Route` belongs.
 */

export default function findClashingUrlPatterns(
  routeBundles: Array<RouteBundle>
): Array<[[string, string], [string, string]]> {
  const clashingPairs: Array<[[string, string], [string, string]]> = [];
  for (let i = 0; i < routeBundles.length - 1; i++) {
    const bundle1 = routeBundles[i];
    for (let j = i + 1; j < routeBundles.length; j++) {
      const bundle2 = routeBundles[j];
      for (const route1 of bundle1.routes) {
        const pattern1 = route1.urlPattern;
        if (!isWellFormedUrlPattern(pattern1)) {
          throw new Error(`${pattern1} is not well-formed`);
        }
        for (const route2 of bundle2.routes) {
          const pattern2 = route2.urlPattern;
          if (!isWellFormedUrlPattern(pattern2)) {
            throw new Error(`${pattern2} is not well-formed`);
          }
          if (
            !(route1.legacy === true && route2.legacy === true) &&
            isPrecedenceClashingPair(pattern1, pattern2)
          ) {
            clashingPairs.push([
              [pattern1, bundle1.domain],
              [pattern2, bundle2.domain],
            ]);
          }
        }
      }
    }
  }
  return clashingPairs;
}

/**
 * @param urlPattern1 a url-pattern, in Backbone url-pattern-matching language, assumed to be well-formed
 * according to the `isWellFormedUrlPattern` function.
 * @param urlPattern2 another url-pattern
 * @return `true` if there exists a string that matches to both patterns, `false` otherwise.
 *
 * This code relies on the following (things I claim to be) facts:
 *  - A pattern of the form A(B)C clashes with a pattern D iff ABC clashes with D or AC clashes with D.
 *  - A pattern of the form A*B clashes with a pattern C iff A clashes with any prefix of C.
 *  - A pattern of the form A1/A2/.../An, containing no parentheses or stars, and where for all i, Ai contains
 * no slashes, clashes with a similar pattern of the form B1/B2/.../Bm, iff n = m and for all i, Ai clashes
 * with Bi.
 *  - A pattern of the form :A, containing no other special characters always clashes with a non-empty pattern
 * containing only non-special characters or `:`. It does not clash with an empty pattern (according to the
 * testing I did).
 *  - Two patterns containing no special characters clash iff they are identical.
 */
function isPrecedenceClashingPair(urlPattern1: string, urlPattern2: string): boolean {
  const pattern1Variants = getVariants(urlPattern1);
  const pattern2Variants = getVariants(urlPattern2);
  for (const variant1 of pattern1Variants) {
    for (const variant2 of pattern2Variants) {
      if (isClashingNoParensPair(variant1, variant2)) {
        return true;
      }
    }
  }
  return false;
}

/**
 * @param urlPattern a url-pattern in Backbone url-pattern-matching language, assumed to be well-formed.
 * @returns an array containing every url-pattern obtainable by either including or not including parts
 * contained in parentheses in `urlPattern`. Let n be the number of pairs of parentheses in `urlPattern`. The
 * return array will have 2^n members. To make sure the performance of this function is reasonable, we limit
 * the number of parentheses we consider well-formed to 5. (See `isWellFormedUrlPattern.ts`)
 */
function getVariants(urlPattern: string): Array<string> {
  let variants = [''];
  let inside = false;
  for (let i = 0; i < urlPattern.length; i++) {
    const char = urlPattern[i];
    switch (char) {
      case '(':
        inside = true;
        variants = variants.concat(variants);
        break;
      case ')':
        inside = false;
        break;
      default:
        for (let j = inside ? Math.floor(variants.length / 2) : 0; j < variants.length; j++) {
          variants[j] += char;
        }
    }
  }
  return variants;
}

/**
 * @param urlPattern1 a url-pattern, in Backbone url-pattern-matching language, assumed to be well-formed and
 * contain no parentheses
 * @param urlPattern2 same as `urlPattern1`
 * @return `true` if there exists a string that matches to both patterns, `false` otherwise.
 */
function isClashingNoParensPair(urlPattern1: string, urlPattern2: string): boolean {
  const pathPartPattern = /([:a-z0-9\-_]+\/)/;
  const pathParts1 = urlPattern1.split(pathPartPattern);
  const pathParts2 = urlPattern2.split(pathPartPattern);
  const terminalPart1 = pathParts1[pathParts1.length - 1];
  const terminalPart2 = pathParts2[pathParts2.length - 1];
  let prefixToCheckLength;
  if (terminalPart1[0] === '*' && terminalPart2[0] === '*') {
    prefixToCheckLength = Math.min(pathParts1.length - 1, pathParts2.length - 1);
  } else if (terminalPart1[0] === '*') {
    prefixToCheckLength = pathParts1.length - 1;
  } else if (terminalPart2[0] === '*') {
    prefixToCheckLength = pathParts2.length - 1;
  } else {
    prefixToCheckLength = Math.max(pathParts1.length, pathParts2.length);
  }
  return isClashingPathNoParensNoStar(
    pathParts1.slice(0, prefixToCheckLength),
    pathParts2.slice(0, prefixToCheckLength)
  );
}

/**
 * @param pathParts1 an array of url-patterns, in Backbone pattern-matching language, assumed to be
 * well-formed, and contain no special characters other than `:`
 * @param pathParts2 same as pathParts1
 * @returns `true` if there exists a string that matches to the concatenation of these patterns, `false`
 * otherwise.
 */
function isClashingPathNoParensNoStar(pathParts1: Array<string>, pathParts2: Array<string>): boolean {
  if (pathParts1.length !== pathParts2.length) {
    return false;
  }
  for (let i = 0; i < pathParts1.length; i++) {
    if (isClashingPathPartPair(pathParts1[i], pathParts2[i]) === false) {
      return false;
    }
  }
  return true;
}

/**
 * @param pathPart1 a url-pattern, in Backbone pattern-matching language, assumed to be well-formed, and
 * contain no special characters other than `:`
 * @param pathPart2 same as pathPart1
 * @return `true` if there exists a string that matches both of these patterns, `false` otherwise.
 */
function isClashingPathPartPair(pathPart1: string, pathPart2: string): boolean {
  if ((pathPart1[0] === ':' || pathPart2[0] === ':') && pathPart1 !== '' && pathPart2 !== '') {
    return true;
  } else {
    return pathPart1 === pathPart2;
  }
}
